import React, { useEffect, useState } from 'react';
import driverApi from '../../clients/DriverClient';
import { type Membership } from '../../models/Membership';
import { EntitlementGrantStatus } from '../../models/entitlement/EntitlementSummary';
import { Link } from 'react-router-dom';
import EntitlementCard from './EntitlementCard';

interface DashboardActionsProps {
    driverId?: string;
    firstName?: string;
}

const DashboardActions: React.FC<DashboardActionsProps> = ({ driverId, firstName }) => {
    const [membership, setMembership] = useState<Membership | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');

    useEffect(() => {
        const fetchMembership = async () => {
            if (!driverId) {
                setMembership({ id: '', name: 'None', bookingLinks: [] });
                return;
            }
            setIsLoading(true);
            setError('');
            try {
                const result = await driverApi.getMembershipByDriverId(driverId);
                setMembership(result);
            } catch (e) {
                setError('Failed to load membership');
            } finally {
                setIsLoading(false);
            }
        };
        fetchMembership();
    }, [driverId]);

    const hasActiveEntitlement =
        membership?.entitlement?.status === EntitlementGrantStatus.Active;
    const hasMembership = !!membership && membership.name !== 'None';

    // The driver-info-main block (heading + subtitle) is always rendered so the
    // page always has an <h1>, regardless of fetch state. Only the card and
    // actions row are gated on the loading / error / loaded state.
    return (
        <div className="driver-info-header">
            <div className="driver-info-main">
                <h1 className="mb-3 heading">Welcome back, {firstName}!</h1>
                <p className="text-paragraphs mb-0">
                    This is your driver dashboard where you can manage your account and view important information.
                </p>
            </div>

            {isLoading && (
                <div className="driver-info-actions text-center py-4">
                    <span className="spinner-border text-primary" role="status" aria-hidden="true"></span>
                </div>
            )}

            {!isLoading && error && (
                <div className="driver-info-actions alert alert-danger" role="alert">{error}</div>
            )}

            {!isLoading && !error && (
                <>
                    {/* The three children below are direct siblings so CSS grid areas
                        can reorder them: on mobile the card (aside) sits between the
                        text (main) and the actions row; on desktop main+actions stack
                        on the left, card on the right. */}
                    {hasActiveEntitlement && (
                        <div className="driver-info-aside">
                            <EntitlementCard membership={membership} />
                        </div>
                    )}

                    <div className="driver-info-actions">
                        {/* Show the membership tier badge only when there is no active
                            entitlement card (which already shows the name) but the member
                            still has a membership. */}
                        {!hasActiveEntitlement && hasMembership && (
                            <div className="d-flex align-items-center gap-2">
                                <span className="badge">Membership:</span>
                                <span className="badge bg-success">{membership?.name}</span>
                            </div>
                        )}
                        <Link
                            to="/booking"
                            className="btn btn-sm btn-primary text-nowrap dashboard-action-btn"
                        >
                            Book with Credits
                        </Link>
                    </div>
                </>
            )}
        </div>
    );
};

export default DashboardActions;
